/**
 * RelicInventoryDialog — Shows the player's relic inventory with
 * tier-colored badges, status indicators, and research actions.
 * Follows the MineralInventoryDialog pattern exactly.
 */

import Phaser from 'phaser';
import { Colors, CSSColors, Fonts, FontSize, Radius, Depth, createDialogChrome, closeDialog } from './GameUITheme';
import { getApiBaseUrl } from '../../utils/ApiConfig';
import { isMobile } from '../../utils/ResponsiveHelper';
import { I18n } from '../../i18n/I18nService';

const API_URL = getApiBaseUrl();

const PAD = 12;

// ── Tier colors (matches TECH_RESEARCH.md spec) ───────────────
const TIER_COLORS: Record<number, { hex: number; css: string; label: string }> = {
  1: { hex: 0xffffff, css: '#ffffff', label: 'Common' },
  2: { hex: 0x44ff44, css: '#44ff44', label: 'Uncommon' },
  3: { hex: 0x4488ff, css: '#4488ff', label: 'Rare' },
  4: { hex: 0xaa44ff, css: '#aa44ff', label: 'Epic' },
  5: { hex: 0xffcc00, css: '#ffcc00', label: 'Legendary' },
};

// ── Status colors ─────────────────────────────────────────────
const STATUS_COLORS: Record<string, { css: string; label: string }> = {
  inventory:   { css: '#00ffff', label: 'INVENTORY' },
  researching: { css: '#ffcc00', label: 'RESEARCHING' },
  consumed:    { css: '#667788', label: 'CONSUMED' },
};

// ── Source labels ─────────────────────────────────────────────
const SOURCE_LABELS: Record<string, string> = {
  mining:        'Mining',
  wreck_salvage: 'Wreck Salvage',
  starter:       'Starter',
  pirate_base:   'Pirate Base',
  quest:         'Quest',
};

// ── Status sort order (inventory first, then researching, then consumed) ──
const STATUS_ORDER: Record<string, number> = {
  inventory: 0, researching: 1, consumed: 2,
};

// ── Relic data shape from GET /api/research/relics/inventory ──
interface RelicItem {
  id: string;
  technologyId: string;
  technologyName: string;
  technologyDescription?: string;
  tier: number;
  category: string;
  source: string;
  sourceDetails?: Record<string, unknown>;
  status: string;
  discoveredAt: string;
  consumedAt?: string;
  relicName: string;
  relicDescription?: string;
  // Technology detail fields
  unlocks?: string;
  prerequisites?: Array<{ id: string; name: string }>;
  isdCost?: number;
  mineralCosts?: Record<string, number>;
  researchTicks?: number;
}

export class RelicInventoryDialog extends Phaser.GameObjects.Container {
  private mobile: boolean;
  private listW: number;
  private detailX: number;
  private detailW: number;
  private relics: RelicItem[] = [];
  private selectedRelic: RelicItem | null = null;

  private listContainer: Phaser.GameObjects.Container;
  private detailContainer: Phaser.GameObjects.Container;
  private statusText: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene) {
    const mobile = isMobile(scene);
    const dw = mobile ? 340 : 652;
    const dh = mobile ? 540 : 560;
    const LIST_W = mobile ? dw - PAD * 2 : 260;
    const { width, height } = scene.cameras.main;
    super(scene, width / 2 - dw / 2, height / 2 - dh / 2);
    this.mobile = mobile;
    this.listW = LIST_W;
    this.detailX = mobile ? PAD : LIST_W + PAD * 2;
    this.detailW = mobile ? dw - PAD * 2 : dw - (LIST_W + PAD * 2) - PAD;

    createDialogChrome(scene, this, dw, dh, {
      title: I18n.t('dialog.relic_inventory'),
      onClose: () => this.close(),
    });

    // Left: relic list
    this.listContainer = scene.add.container(PAD, 58);
    this.add(this.listContainer);

    // Right: detail panel (overlay on mobile, side-by-side on desktop)
    this.detailContainer = scene.add.container(this.detailX, 58);
    if (mobile) this.detailContainer.setVisible(false);
    this.add(this.detailContainer);

    // Status text (bottom right)
    this.statusText = scene.add.text(dw - PAD, dh - 15, '', {
      fontSize: FontSize.xs, color: '#556677', fontFamily: Fonts.body,
    });
    this.statusText.setOrigin(1, 0.5);
    this.add(this.statusText);

    this.setDepth(Depth.overlay);
    scene.add.existing(this);

    this.loadData();
  }

  private close(): void {
    closeDialog(this.scene, this);
  }

  // ── Auth helper ─────────────────────────────────────────────
  private authHeaders(): HeadersInit {
    const token = localStorage.getItem('crimsonmandate_token');
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  // ============ Data Loading ============

  private async loadData(): Promise<void> {
    this.statusText.setText('Loading...');

    try {
      const response = await fetch(`${API_URL}/research/relics/inventory`, {
        headers: this.authHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch relics: ${response.status}`);
      }

      const data = await response.json();
      const rawRelics: RelicItem[] = data.relics || data || [];

      // Sort: inventory first, then researching, then consumed.
      // Within status, sort by tier descending.
      this.relics = rawRelics.sort((a, b) => {
        const sa = STATUS_ORDER[a.status] ?? 3;
        const sb = STATUS_ORDER[b.status] ?? 3;
        if (sa !== sb) return sa - sb;
        // Higher tier first within same status
        if (a.tier !== b.tier) return b.tier - a.tier;
        return (a.technologyName || '').localeCompare(b.technologyName || '');
      });

      this.statusText.setText('');

      // Auto-select first
      if (!this.selectedRelic && this.relics.length > 0) {
        this.selectedRelic = this.relics[0];
      }

      this.renderList();
      this.renderDetail();
    } catch (error) {
      console.error('[RelicInventory] Failed to load relics:', error);
      this.statusText.setText('Failed to load relics');
    }
  }

  // ============ Relic List (left column) ============

  private renderList(): void {
    this.listContainer.removeAll(true);

    if (this.relics.length === 0) {
      const empty = this.scene.add.text(this.listW / 2, 60, 'No relics discovered.\nMine asteroids or\nsalvage wrecks to\nfind relics!', {
        fontSize: FontSize.sm, color: '#556677', fontFamily: Fonts.body, align: 'center',
      });
      empty.setOrigin(0.5, 0);
      this.listContainer.add(empty);
      return;
    }

    let y = 0;
    for (const relic of this.relics) {
      const card = this.createRelicCard(relic, y);
      this.listContainer.add(card);
      y += 52;
    }
  }

  private createRelicCard(relic: RelicItem, y: number): Phaser.GameObjects.Container {
    const isSelected = this.selectedRelic?.id === relic.id;
    const card = this.scene.add.container(0, y);
    const cardH = 48;

    const tierInfo = TIER_COLORS[relic.tier] || TIER_COLORS[1];
    const statusInfo = STATUS_COLORS[relic.status] || STATUS_COLORS.inventory;

    // Background
    const bg = this.scene.add.graphics();
    const drawBg = (selected: boolean, hover: boolean) => {
      bg.clear();
      const fill = selected ? 0x253a66 : hover ? 0x1e2e55 : Colors.bgCard;
      bg.fillStyle(fill, 0.9);
      bg.fillRoundedRect(0, 0, this.listW, cardH, Radius.sm);
      const bColor = selected ? Colors.cyan : hover ? Colors.cyan : Colors.borderDim;
      const bAlpha = selected ? 0.3 : hover ? 0.15 : 0.08;
      bg.lineStyle(1, bColor, bAlpha);
      bg.strokeRoundedRect(0, 0, this.listW, cardH, Radius.sm);
    };
    drawBg(isSelected, false);
    card.add(bg);

    // Tier badge (colored circle)
    const circle = this.scene.add.graphics();
    circle.fillStyle(tierInfo.hex, 1);
    circle.fillCircle(20, cardH / 2, 10);
    circle.lineStyle(1, 0xffffff, 0.2);
    circle.strokeCircle(20, cardH / 2, 10);
    // Tier number inside circle
    const tierNum = this.scene.add.text(20, cardH / 2, `${relic.tier}`, {
      fontSize: '10px', color: relic.tier >= 4 ? '#000000' : '#000000', fontFamily: Fonts.heading,
      fontStyle: '900',
    });
    tierNum.setOrigin(0.5);
    card.add(circle);
    card.add(tierNum);

    // Relic name (from technology name)
    const nameText = this.scene.add.text(38, 6, relic.relicName || relic.technologyName, {
      fontSize: FontSize.sm,
      color: isSelected ? CSSColors.textWhite : tierInfo.css,
      fontFamily: isSelected ? Fonts.heading : Fonts.body,
    });
    card.add(nameText);

    // Source text + status badge on second line
    const sourceLabel = SOURCE_LABELS[relic.source] || relic.source;
    const sourceText = this.scene.add.text(38, 26, sourceLabel, {
      fontSize: FontSize.xs, color: '#889999', fontFamily: Fonts.body,
    });
    card.add(sourceText);

    // Status badge (right side)
    const statusLabel = this.scene.add.text(this.listW - 8, cardH / 2, statusInfo.label, {
      fontSize: '10px', color: statusInfo.css, fontFamily: Fonts.heading,
    });
    statusLabel.setOrigin(1, 0.5);
    card.add(statusLabel);

    // Interactive zone
    const hitZone = this.scene.add.zone(0, 0, this.listW, cardH).setOrigin(0, 0);
    hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, this.listW, cardH), Phaser.Geom.Rectangle.Contains);
    hitZone.on('pointerover', () => {
      if (!isSelected) drawBg(false, true);
      this.scene.input.setDefaultCursor('pointer');
    });
    hitZone.on('pointerout', () => {
      drawBg(isSelected, false);
      this.scene.input.setDefaultCursor('default');
    });
    hitZone.on('pointerdown', () => {
      this.selectedRelic = relic;
      this.renderList();
      this.renderDetail();
      if (this.mobile) {
        this.listContainer.setVisible(false);
        this.detailContainer.setVisible(true);
      }
    });
    card.add(hitZone);

    return card;
  }

  // ============ Detail Panel (right column) ============

  private renderDetail(): void {
    this.detailContainer.removeAll(true);

    if (!this.selectedRelic) {
      const hint = this.scene.add.text(this.detailW / 2, 60, 'Select a relic\nto view details', {
        fontSize: FontSize.sm, color: '#556677', fontFamily: Fonts.body, align: 'center',
      });
      hint.setOrigin(0.5, 0);
      this.detailContainer.add(hint);
      return;
    }

    const relic = this.selectedRelic;
    const tierInfo = TIER_COLORS[relic.tier] || TIER_COLORS[1];
    const statusInfo = STATUS_COLORS[relic.status] || STATUS_COLORS.inventory;

    let y = 0;

    // Large tier icon + technology name
    const bigCircle = this.scene.add.graphics();
    bigCircle.fillStyle(tierInfo.hex, 1);
    bigCircle.fillCircle(22, 22, 18);
    bigCircle.lineStyle(2, 0xffffff, 0.25);
    bigCircle.strokeCircle(22, 22, 18);
    this.detailContainer.add(bigCircle);

    // Tier number in big circle
    const bigTierNum = this.scene.add.text(22, 22, `T${relic.tier}`, {
      fontSize: FontSize.sm, color: '#000000', fontFamily: Fonts.heading, fontStyle: '900',
    });
    bigTierNum.setOrigin(0.5);
    this.detailContainer.add(bigTierNum);

    const titleText = this.scene.add.text(50, 4, relic.technologyName, {
      fontSize: FontSize.xl, color: tierInfo.css, fontFamily: Fonts.heading,
    });
    this.detailContainer.add(titleText);

    // Tier + Category
    const tierCatText = this.scene.add.text(50, 26, `Tier ${relic.tier} ${tierInfo.label} \u2022 ${relic.category || 'Unknown'}`, {
      fontSize: FontSize.xs, color: tierInfo.css, fontFamily: Fonts.heading,
    });
    this.detailContainer.add(tierCatText);
    y += 50;

    // Description
    if (relic.technologyDescription) {
      const desc = this.scene.add.text(0, y, relic.technologyDescription, {
        fontSize: FontSize.xs, color: '#667788', fontFamily: Fonts.body,
        wordWrap: { width: this.detailW }, lineSpacing: 2,
      });
      this.detailContainer.add(desc);
      y += desc.height + 10;
    }

    // Divider
    this.addDivider(y); y += 10;

    // Status
    this.addInfoRow(0, y, 'Status', statusInfo.label, statusInfo.css);
    y += 22;

    // Source
    const sourceLabel = SOURCE_LABELS[relic.source] || relic.source;
    this.addInfoRow(0, y, 'Source', sourceLabel, '#889999');
    y += 22;

    // Discovered date
    if (relic.discoveredAt) {
      const date = new Date(relic.discoveredAt);
      this.addInfoRow(0, y, 'Discovered', date.toLocaleDateString(), '#889999');
      y += 22;
    }

    // What it unlocks
    if (relic.unlocks) {
      this.addInfoRow(0, y, 'Unlocks', relic.unlocks, CSSColors.cyan);
      y += 22;
    }

    // Prerequisites
    if (relic.prerequisites && relic.prerequisites.length > 0) {
      const prereqNames = relic.prerequisites.map(p => p.name).join(', ');
      const prereqText = this.scene.add.text(0, y, `Prerequisites: ${prereqNames}`, {
        fontSize: FontSize.xs, color: '#889999', fontFamily: Fonts.body,
        wordWrap: { width: this.detailW }, lineSpacing: 2,
      });
      this.detailContainer.add(prereqText);
      y += prereqText.height + 6;
    }

    // Divider
    y += 4;
    this.addDivider(y); y += 10;

    // Research cost section
    const costHeader = this.scene.add.text(0, y, 'RESEARCH COST', {
      fontSize: FontSize.xs, color: '#5588aa', fontFamily: Fonts.heading,
    });
    this.detailContainer.add(costHeader);
    y += 18;

    if (relic.isdCost !== undefined && relic.isdCost > 0) {
      this.addInfoRow(0, y, '$ISD', `$${relic.isdCost.toLocaleString()}`, CSSColors.gold);
      y += 20;
    }

    if (relic.mineralCosts && Object.keys(relic.mineralCosts).length > 0) {
      for (const [mineral, amount] of Object.entries(relic.mineralCosts)) {
        this.addInfoRow(0, y, mineral.charAt(0).toUpperCase() + mineral.slice(1), `x${(amount as number).toLocaleString()}`, '#aaccdd');
        y += 20;
      }
    }

    if (relic.researchTicks && relic.researchTicks > 0) {
      const hours = Math.round(relic.researchTicks / 3600 * 10) / 10;
      const durationStr = hours >= 1 ? `${hours}h` : `${Math.round(relic.researchTicks / 60)}m`;
      this.addInfoRow(0, y, 'Duration', durationStr, '#889999');
      y += 24;
    }

    // Action buttons (only for inventory status relics)
    if (relic.status === 'inventory') {
      // Research at Earth button
      this.detailContainer.add(
        this.createActionBtn(0, y, this.detailW, 36, 'Research at Earth', 0x1a2a44, () => {
          this.startResearch(relic.id, 'earth');
        }),
      );
      y += 42;

      // Research at Station button
      this.detailContainer.add(
        this.createActionBtn(0, y, this.detailW, 36, 'Research at Station', 0x1a4422, () => {
          this.startResearch(relic.id, 'station');
        }),
      );
    } else if (relic.status === 'researching') {
      const inProgress = this.scene.add.text(this.detailW / 2, y + 10, 'Research in progress...', {
        fontSize: FontSize.sm, color: CSSColors.gold, fontFamily: Fonts.heading,
      });
      inProgress.setOrigin(0.5, 0);
      this.detailContainer.add(inProgress);
    } else if (relic.status === 'consumed') {
      const consumed = this.scene.add.text(this.detailW / 2, y + 10, 'Technology unlocked', {
        fontSize: FontSize.sm, color: '#556677', fontFamily: Fonts.heading,
      });
      consumed.setOrigin(0.5, 0);
      this.detailContainer.add(consumed);
    }
  }

  // ============ Research Action ============

  private async startResearch(relicId: string, locationType: 'earth' | 'station'): Promise<void> {
    this.statusText.setText('Starting research...');

    try {
      const response = await fetch(`${API_URL}/research/start`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ relicId, locationType }),
      });

      const data = await response.json();

      if (!response.ok) {
        this.statusText.setText(data.error || 'Failed to start research');
        return;
      }

      this.statusText.setText('Research started!');
      // Reload data to reflect status change
      this.loadData();
    } catch (error) {
      console.error('[RelicInventory] Failed to start research:', error);
      this.statusText.setText('Failed to start research');
    }
  }

  // ============ Helpers ============

  private addDivider(y: number): void {
    const g = this.scene.add.graphics();
    g.lineStyle(1, Colors.cyan, 0.08);
    g.lineBetween(0, y, this.detailW, y);
    this.detailContainer.add(g);
  }

  private addInfoRow(x: number, y: number, label: string, value: string, valueColor: string): void {
    const lbl = this.scene.add.text(x, y, `${label}: `, {
      fontSize: FontSize.xs, color: '#889999', fontFamily: Fonts.body,
    });
    this.detailContainer.add(lbl);

    const val = this.scene.add.text(x + lbl.width, y, value, {
      fontSize: FontSize.xs, color: valueColor, fontFamily: Fonts.body,
    });
    this.detailContainer.add(val);
  }

  private createActionBtn(
    x: number, y: number, w: number, h: number,
    label: string, color: number, callback: () => void,
  ): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(x, y);

    const bg = this.scene.add.graphics();
    bg.fillStyle(color, 1);
    bg.fillRoundedRect(0, 0, w, h, Radius.sm);
    bg.lineStyle(1, Colors.cyan, 0.15);
    bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
    btn.add(bg);

    const text = this.scene.add.text(w / 2, h / 2, label, {
      fontSize: FontSize.sm, color: CSSColors.textWhite, fontFamily: Fonts.heading,
    });
    text.setOrigin(0.5);
    btn.add(text);

    const hitZone = this.scene.add.zone(0, 0, w, h).setOrigin(0, 0);
    hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);
    hitZone.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(color + 0x111111, 1);
      bg.fillRoundedRect(0, 0, w, h, Radius.sm);
      bg.lineStyle(1, Colors.cyan, 0.3);
      bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
      this.scene.input.setDefaultCursor('pointer');
    });
    hitZone.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(color, 1);
      bg.fillRoundedRect(0, 0, w, h, Radius.sm);
      bg.lineStyle(1, Colors.cyan, 0.15);
      bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
      this.scene.input.setDefaultCursor('default');
    });
    hitZone.on('pointerdown', callback);
    btn.add(hitZone);

    return btn;
  }
}
